/**
 * THE FEE CALCULATOR, AS A COMPONENT (Add instruction redesign, Karan
 * 26/10/2026 — design pages 3, 5, 6 and 7). Add instruction and Edit
 * instruction mount it; the maths underneath is public/fee-calc.js, the same
 * functions the enquiry's calculator prices with, and it saves the same line
 * shape (feeCalc.lines).
 *
 *   Fee   £60/h · each line to the nearest £5   [Add from the catalogue…]  ✦ Draft with AI
 *   ITEM                                  NO   CX   HOURS   FEE
 *   ⠿ Steel Beam Design                    2   1.0   4:00   £240  ×
 *     Structural · 2:00 std each
 *   Often priced with Steel Beam Design:  + Padstone Design · on 8 in 10 projects
 *   Revisions  1:30 each · 20% of design  Charged|Included   1  20%  1:30  included
 *   TOTAL  How fees are calculated                                9:14   £460
 *
 * The catalogue search (page 5): every match shows its service, how many
 * projects it was priced on, its std hours and the fee for one; sub'd work in
 * its own group; ↑ ↓ choose, ↵ adds and puts the cursor in No, Esc closes. No
 * match (page 7): add it to the catalogue (name in Title Case, service, std
 * hours, per) — or a one-off line for this project only, marked as one.
 *
 * Everything shown from data goes through escapeHtml. The component never
 * writes to Firestore itself: the catalogue add is the caller's onAddToCatalogue.
 */
import * as FC from '../fee-calc.js';
import { searchCatalogue, lineFromEntry, titleCaseName, keyOf } from '../fee-catalogue.js';
import { escapeHtml, hoursHM, money, money2 } from '../ui.js';
import { navIcon } from '../nav-icons.js';

const PER_UNITS = ['each', 'item', 'visit', 'day', 'sheet', 'drawing', 'beam', 'opening'];

/** "1:30", "1.5", "90m" → hours. */
export function parseHours(v) {
  const s = String(v == null ? '' : v).trim();
  let m = /^(\d+):(\d{1,2})$/.exec(s);
  if (m) return Number(m[1]) + Number(m[2]) / 60;
  m = /^(\d+)\s*m$/i.exec(s);
  if (m) return Number(m[1]) / 60;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** The text with every occurrence of the query's words wrapped in <b> — escaped piecewise. */
function highlight(text, q) {
  const words = keyOf(q).split(' ').filter(Boolean);
  const t = String(text || '');
  if (!words.length) return escapeHtml(t);
  const low = t.toLowerCase();
  const marks = new Array(t.length).fill(false);
  for (const w of words) { let i = low.indexOf(w); while (i !== -1) { for (let k = i; k < i + w.length; k += 1) marks[k] = true; i = low.indexOf(w, i + w.length); } }
  let out = '', run = '', on = false;
  for (let i = 0; i < t.length; i += 1) {
    if (marks[i] !== on) { out += on ? `<b>${escapeHtml(run)}</b>` : escapeHtml(run); run = ''; on = marks[i]; }
    run += t[i];
  }
  return out + (on ? `<b>${escapeHtml(run)}</b>` : escapeHtml(run));
}

/**
 * mountFeeCalculator(host, opts) → controller.
 * opts: rate, catalogue (fee-catalogue entries), services (names),
 *   lines, revisions, canAddToCatalogue, onAddToCatalogue({ name, service, hours, unit }) → entry,
 *   onDraftWithAi() → lines | null, aiTitle, onChange(state), readOnly.
 */
export function mountFeeCalculator(host, opts = {}) {
  const rate = Number(opts.rate) || 60;
  let catalogue = Array.isArray(opts.catalogue) ? opts.catalogue.slice() : [];
  const services = Array.isArray(opts.services) ? opts.services : [];
  let lines = (opts.lines || []).map((l) => ({ ...l }));
  let rev = Object.assign({ count: 1, percent: 20, charged: false }, opts.revisions || {});
  let cooccur = [];
  const ro = !!opts.readOnly;
  const ui = { q: '', open: false, active: 0, adding: false, how: false, aiBusy: false, aiNote: '' };
  const byKey = () => new Map(catalogue.map((e) => [e.key, e]));

  const totals = () => {
    const hours = FC.budgetHours(lines) + FC.revTotalHours(lines, rev);
    const fee = FC.calcTotalFee(lines, rate) + FC.revFee(lines, rev, rate);
    return { hours, fee };
  };
  const state = () => ({ lines: lines.map((l) => ({ ...l })), revisions: { ...rev }, ...totals() });
  const changed = () => { if (typeof opts.onChange === 'function') opts.onChange(state()); };

  host.classList.add('fcx');
  host.innerHTML = `
    <div class="fcx-head">
      <span class="fcx-title">Fee</span>
      <span class="fcx-rate">£${escapeHtml(String(rate))}/h · each line to the nearest £5</span>
      ${ro ? '' : `<div class="fcx-search">
        <span class="fcx-search-ic" aria-hidden="true">${navIcon('search', 14)}</span>
        <input class="fcx-q" type="text" autocomplete="off" placeholder="Add from the catalogue…" aria-label="Add from the catalogue" aria-expanded="false" aria-controls="fcx-drop" role="combobox">
        <div class="fcx-drop" id="fcx-drop" role="listbox" hidden></div>
      </div>
      <button type="button" class="btn fcx-ai"${opts.onDraftWithAi ? '' : ' disabled'} title="${escapeHtml(opts.aiTitle || 'Draft the lines from the brief — the model picks the items, the code does the money')}">✦ Draft with AI</button>`}
    </div>
    <div class="fcx-ainote" hidden></div>
    <div class="fcx-table" role="table" aria-label="Fee lines"></div>`;
  const table = host.querySelector('.fcx-table');
  const qIn = host.querySelector('.fcx-q');
  const drop = host.querySelector('.fcx-drop');
  const aiBtn = host.querySelector('.fcx-ai');
  const aiNote = host.querySelector('.fcx-ainote');

  // ── the lines ────────────────────────────────────────────────────────────
  const subLine = (l) => {
    if (FC.lineIsSub(l)) return `Sub’d · ${escapeHtml(l.supplierName || 'supplier')} · ${escapeHtml(money2(FC.lineSubChargeP(l)))} a ${escapeHtml(l.unitLabel || 'unit')}${FC.lineStd(l) ? ` + ${escapeHtml(hoursHM(FC.lineStd(l)))} ours` : ''}`;
    if (FC.lineIsComm(l)) return 'Internal — never shown to the client';
    const std = FC.lineStd(l);
    if (l.oneOff) {
      return `<span class="fcx-oneoff">One-off</span> · ${ro ? escapeHtml(hoursHM(std)) : `<input class="fcx-std" data-std="${lines.indexOf(l)}" value="${escapeHtml(hoursHM(std))}" aria-label="Std hours for ${escapeHtml(l.item)}">`} std each`;
    }
    const svc = l.service || (byKey().get(keyOf(l.item)) || {}).service || '';
    return `${svc ? `${escapeHtml(svc)} · ` : ''}${escapeHtml(hoursHM(std))} std each`;
  };
  const rowHtml = (l, i) => {
    const sub = FC.lineIsSub(l), comm = FC.lineIsComm(l);
    const dis = ro ? ' disabled' : '';
    const no = comm ? '<span class="fcx-dash">—</span>'
      : `<input class="fcx-in" data-qty="${i}" type="number" min="0" step="${sub ? '0.5' : '1'}" inputmode="decimal" value="${escapeHtml(String(Number(l.qty) || 1))}" aria-label="Number of ${escapeHtml(l.item)}"${dis}>`;
    const cx = (comm || sub) ? '<span class="fcx-dash">—</span>'
      : `<input class="fcx-in" data-cx="${i}" type="number" min="0.1" step="0.1" inputmode="decimal" value="${escapeHtml((Number(l.complexity) || 1).toFixed(1))}" aria-label="Complexity for ${escapeHtml(l.item)}" title="Complexity — scales the hours and the fee"${dis}>`;
    const fee = comm ? (ro ? escapeHtml(money(Number(l.fee) || 0))
      : `<input class="fcx-in fcx-in-fee" data-cfee="${i}" type="number" min="0" step="1" value="${escapeHtml(String(Math.round(Number(l.fee) || 0)))}" aria-label="Commission £">`)
      : escapeHtml(money(FC.lineEffFee(l, rate)));
    return `<div class="fcx-row fcx-grid" role="row" data-row="${i}"${ro ? '' : ' draggable="true"'}>
      <span class="fcx-item" role="cell">
        ${ro ? '' : '<span class="fcx-handle" aria-hidden="true" title="Drag to reorder">⠿</span>'}
        <span class="fcx-iname">${escapeHtml(l.item || '—')}${l.asQuoted ? ' <span class="fcx-tag">As quoted</span>' : ''}</span>
        <span class="fcx-isub">${subLine(l)}</span>
      </span>
      <span role="cell" class="fcx-c">${no}</span>
      <span role="cell" class="fcx-c">${cx}</span>
      <span role="cell" class="fcx-num">${comm ? '—' : escapeHtml(hoursHM(FC.lineEffHours(l)))}</span>
      <span role="cell" class="fcx-num fcx-fee">${fee}</span>
      <span role="cell" class="fcx-x-c">${ro ? '' : `<button type="button" class="fcx-x" data-del="${i}" aria-label="Remove ${escapeHtml(l.item)}">×</button>`}</span>
    </div>`;
  };
  // "Often priced with" (page 6): under the line it came from, for the first
  // line that has suggestions not already on the quote and in the catalogue.
  const suggestHtml = () => {
    if (ro || !cooccur.length) return { at: -1, html: '' };
    const onQuote = new Set(lines.map((l) => keyOf(l.item)));
    const cat = byKey();
    for (const c of cooccur) {
      const at = lines.findIndex((l) => keyOf(l.item) === c.key);
      if (at < 0) continue;
      const picks = (c.with || []).filter((w) => !onQuote.has(w.key) && cat.has(w.key));
      if (!picks.length) continue;
      const chips = picks.map((w, k) => {
        const tenths = Math.max(1, Math.round((Number(w.share) || 0) * 10));
        return `<button type="button" class="fcx-chip" data-suggest="${escapeHtml(w.key)}">+ <b>${escapeHtml(cat.get(w.key).name)}</b> <span>· ${k === 0 ? `on ${tenths} in 10 projects` : `${tenths} in 10`}</span></button>`;
      }).join('');
      return { at, html: `<div class="fcx-suggest" role="row"><span class="fcx-suggest-l">Often priced with <b>${escapeHtml(lines[at].item)}</b>:</span>${chips}</div>` };
    }
    return { at: -1, html: '' };
  };

  function draw() {
    const t = totals();
    const each = FC.revHoursEach(lines, rev);
    const revCount = Number(rev.count) || 0;
    const sg = suggestHtml();
    const rows = lines.map((l, i) => rowHtml(l, i) + (i === sg.at ? sg.html : '')).join('');
    table.innerHTML = `
      <div class="fcx-grid fcx-th" role="row"><span role="columnheader">Item</span><span role="columnheader" class="r">No</span><span role="columnheader" class="r">Cx</span><span role="columnheader" class="r">Hours</span><span role="columnheader" class="r">Fee</span><span></span></div>
      ${rows || `<div class="fcx-empty">${ro ? 'No priced lines.' : 'No lines yet — search the catalogue above, or Draft with AI.'}</div>`}
      <div class="fcx-row fcx-grid fcx-rev" role="row">
        <span class="fcx-item" role="cell"><span class="fcx-iname">Revisions</span>
          <span class="fcx-isub">${escapeHtml(hoursHM(each))} each · ${escapeHtml(String(Number(rev.percent) || 0))}% of design
            <span class="fcx-seg" role="group" aria-label="Revisions charged or included">
              <button type="button" data-rev-charged="1" class="${rev.charged ? 'on' : ''}" aria-pressed="${rev.charged}"${ro ? ' disabled' : ''}>Charged</button><button type="button" data-rev-charged="0" class="${rev.charged ? '' : 'on'}" aria-pressed="${!rev.charged}"${ro ? ' disabled' : ''}>Included</button>
            </span></span></span>
        <span role="cell" class="fcx-c"><input class="fcx-in" data-rev-count type="number" min="0" step="1" value="${escapeHtml(String(revCount))}" aria-label="How many revisions"${ro ? ' disabled' : ''}></span>
        <span role="cell" class="fcx-c fcx-pct"><input class="fcx-in" data-rev-pct type="number" min="0" max="100" step="5" value="${escapeHtml(String(Number(rev.percent) || 0))}" aria-label="Each revision as a % of design hours"${ro ? ' disabled' : ''}><span aria-hidden="true">%</span></span>
        <span role="cell" class="fcx-num">${escapeHtml(hoursHM(FC.revTotalHours(lines, rev)))}</span>
        <span role="cell" class="fcx-num fcx-muted">${revCount > 0 ? (rev.charged ? escapeHtml(money(FC.revFee(lines, rev, rate))) : 'included') : '—'}</span>
        <span></span>
      </div>
      <div class="fcx-grid fcx-total" role="row">
        <span class="fcx-total-l" role="cell"><span class="fcx-caps">Total</span><button type="button" class="fcx-how">How fees are calculated</button></span>
        <span></span><span></span>
        <span role="cell" class="fcx-num fcx-total-n">${escapeHtml(hoursHM(t.hours))}</span>
        <span role="cell" class="fcx-num fcx-total-n">${escapeHtml(money(t.fee))}</span>
        <span></span>
      </div>
      <p class="fcx-howtxt"${ui.how ? '' : ' hidden'}>Std hours come from the catalogue. Each line is std × No × Cx (complexity) hours, × £${escapeHtml(String(rate))} an hour — the charge-out rate in settings — to the nearest £5. Sub’d work is charged at its rate (their price and our arrangement fee), plus our own hours on top. Internal lines (commission) are in the total but never shown to the client. Revisions are budgeted either way; the fee only grows when they are Charged.</p>`;
    wireTable();
  }

  // ── editing ──────────────────────────────────────────────────────────────
  function wireTable() {
    const num = (el) => { const n = Number(el.value); return Number.isFinite(n) ? n : 0; };
    const refresh = (focusSel) => {
      draw(); changed();
      if (focusSel) { const el = table.querySelector(focusSel); if (el) { el.focus(); if (el.select) el.select(); } }
    };
    table.querySelectorAll('[data-qty]').forEach((el) => el.addEventListener('change', () => { lines[+el.dataset.qty].qty = Math.max(0, num(el)); refresh(); }));
    table.querySelectorAll('[data-cx]').forEach((el) => el.addEventListener('change', () => { lines[+el.dataset.cx].complexity = Math.max(0.1, num(el) || 1); refresh(); }));
    table.querySelectorAll('[data-cfee]').forEach((el) => el.addEventListener('change', () => { lines[+el.dataset.cfee].fee = Math.max(0, Math.round(num(el))); refresh(); }));
    table.querySelectorAll('[data-std]').forEach((el) => el.addEventListener('change', () => {
      const h = parseHours(el.value); if (h != null) lines[+el.dataset.std].stdHours = h; refresh();
    }));
    table.querySelectorAll('[data-del]').forEach((b) => b.addEventListener('click', () => { lines.splice(+b.dataset.del, 1); refresh(); qIn && qIn.focus(); }));
    table.querySelectorAll('[data-rev-charged]').forEach((b) => b.addEventListener('click', () => { rev.charged = b.dataset.revCharged === '1'; refresh(); }));
    table.querySelector('[data-rev-count]')?.addEventListener('change', (e) => { rev.count = Math.max(0, Math.round(num(e.target))); refresh(); });
    table.querySelector('[data-rev-pct]')?.addEventListener('change', (e) => { rev.percent = Math.min(100, Math.max(0, num(e.target))); refresh(); });
    table.querySelector('.fcx-how')?.addEventListener('click', () => { ui.how = !ui.how; table.querySelector('.fcx-howtxt').hidden = !ui.how; });
    table.querySelectorAll('[data-suggest]').forEach((b) => b.addEventListener('click', () => {
      const e = byKey().get(b.dataset.suggest);
      if (e) addLine(lineFromEntry(e));
    }));
    // Drag to reorder (the order the lines read in on the letter).
    let from = null;
    table.querySelectorAll('.fcx-row[draggable]').forEach((r) => {
      r.addEventListener('dragstart', (e) => { from = +r.dataset.row; r.classList.add('is-drag'); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(from)); } catch (_) { /* old browsers */ } });
      r.addEventListener('dragend', () => r.classList.remove('is-drag'));
      r.addEventListener('dragover', (e) => { if (from != null) e.preventDefault(); });
      r.addEventListener('drop', (e) => {
        e.preventDefault();
        const to = +r.dataset.row;
        if (from == null || from === to || Number.isNaN(to)) return;
        const [m] = lines.splice(from, 1); lines.splice(to, 0, m); from = null; refresh();
      });
    });
  }

  function addLine(l) {
    lines.push(l);
    draw(); changed();
    // ↵ adds, and the cursor goes to No (page 5).
    const el = table.querySelector(`[data-qty="${lines.length - 1}"]`) || table.querySelector(`[data-cfee="${lines.length - 1}"]`);
    if (el) { el.focus(); if (el.select) el.select(); }
  }

  // ── the catalogue search ─────────────────────────────────────────────────
  const options = () => {
    const r = searchCatalogue(catalogue, ui.q, { limit: 6 });
    const opts2 = [...r.work.map((e) => ({ kind: 'item', e })), ...r.subbed.map((e) => ({ kind: 'item', e }))];
    if (ui.q.trim()) { if (opts.canAddToCatalogue) opts2.push({ kind: 'add' }); opts2.push({ kind: 'oneoff' }); }
    return { r, list: opts2 };
  };
  function drawDrop() {
    if (!drop) return;
    if (!ui.open) { drop.hidden = true; qIn.setAttribute('aria-expanded', 'false'); return; }
    const q = ui.q.trim();
    const { r, list } = options();
    if (ui.active >= list.length) ui.active = Math.max(0, list.length - 1);
    const isActive = (o) => list[ui.active] === o;
    const optAt = (pred) => list.find(pred);
    const itemRow = (e) => {
      const o = optAt((x) => x.e === e);
      const meta = e.sub
        ? `Sub’d · per ${escapeHtml(e.sub.unit || 'unit')}${e.sub.suppliers > 1 ? ` · ${e.sub.suppliers} suppliers` : ''}`
        : [e.service ? escapeHtml(e.service) : '', e.uses ? `used on ${e.uses} project${e.uses === 1 ? '' : 's'}` : '', e.note ? escapeHtml(e.note) : ''].filter(Boolean).join(' · ');
      const right = e.sub
        ? `<span class="fcx-subpill">sub’d</span><span class="fcx-o-fee">${escapeHtml(money(Math.round((e.sub.chargePence || 0) / 100)))}</span>`
        : `<span class="fcx-o-std">${escapeHtml(hoursHM(Number(e.hours) || 0))} std</span><span class="fcx-o-fee">${escapeHtml(money(FC.roundFiver((Number(e.hours) || 0) * rate)))} each</span>`;
      return `<div class="fcx-opt${isActive(o) ? ' on' : ''}" role="option" aria-selected="${isActive(o)}" data-opt="${list.indexOf(o)}">
        <span class="fcx-o-main"><span class="fcx-o-name">${highlight(e.name, q)}</span>${meta ? `<span class="fcx-o-meta">${meta}</span>` : ''}</span>
        <span class="fcx-o-right">${right}</span></div>`;
    };
    if (ui.adding) { drawAddForm(); return; }
    let html = '';
    if (!r.total && q) {
      // Page 7: nothing matches — add it to the catalogue, or a one-off line.
      ui.adding = !!opts.canAddToCatalogue;
      if (ui.adding) { drawAddForm(); return; }
      html = `<div class="fcx-d-h">Catalogue · no matches for “${escapeHtml(q)}”</div>`;
    } else {
      html = `<div class="fcx-d-h">Catalogue · ${r.total} match${r.total === 1 ? '' : 'es'}</div>${r.work.map(itemRow).join('')}`;
      if (r.subbed.length) html += `<div class="fcx-d-h fcx-d-h2">Sub’d work</div>${r.subbed.map(itemRow).join('')}`;
    }
    if (q) {
      const addO = optAt((x) => x.kind === 'add'), oneO = optAt((x) => x.kind === 'oneoff');
      html += `<div class="fcx-d-foot">+ Not there? ${addO ? `<button type="button" class="fcx-d-link${isActive(addO) ? ' on' : ''}" data-opt="${list.indexOf(addO)}">Add “${escapeHtml(q)}” to the catalogue</button> · or ` : ''}<button type="button" class="fcx-d-link${isActive(oneO) ? ' on' : ''}" data-opt="${list.indexOf(oneO)}">a one-off line</button></div>`;
    }
    html += '<div class="fcx-d-keys"><kbd>↑</kbd><kbd>↓</kbd> choose <kbd>↵</kbd> add, and the cursor goes to No <kbd>Esc</kbd> close</div>';
    drop.innerHTML = html;
    drop.hidden = false;
    qIn.setAttribute('aria-expanded', 'true');
    drop.querySelectorAll('[data-opt]').forEach((el) => el.addEventListener('mousedown', (ev) => { ev.preventDefault(); choose(list[+el.dataset.opt]); }));
  }
  function drawAddForm(err = '') {
    const q = ui.q.trim();
    const name = titleCaseName(q);
    const last = (name.split(' ').pop() || 'each').toLowerCase();
    const units = [...new Set([last, ...PER_UNITS])].filter(Boolean);
    drop.innerHTML = `<div class="fcx-d-h">Catalogue · no matches for “${escapeHtml(q)}”</div>
      <div class="fcx-addf">
        <p class="fcx-addf-lead"><b>Add it to the catalogue</b> so it’s priced the same way next time.</p>
        <div class="fcx-addf-grid">
          <label><span>Name</span><input class="f-input" data-af="name" value="${escapeHtml(name)}"></label>
          <label><span>Service</span><select class="f-select" data-af="service"><option value="">—</option>${services.map((s) => `<option${s === opts.defaultService ? ' selected' : ''}>${escapeHtml(s)}</option>`).join('')}</select></label>
          <label><span>Std hours</span><input class="f-input fcx-af-std" data-af="hours" value="1:00" inputmode="decimal"></label>
          <label><span>Per</span><select class="f-select" data-af="unit">${units.map((u) => `<option>${escapeHtml(u)}</option>`).join('')}</select></label>
        </div>
        <div class="fcx-addf-foot">
          <span class="fcx-addf-note">${err ? `<span class="fcx-err">${escapeHtml(err)}</span>` : 'Title Case is applied on save. Finance can change std hours later on Catalogue.'}</span>
          <button type="button" class="btn" data-af-oneoff>One-off line instead</button>
          <button type="button" class="btn primary" data-af-add>Add and use</button>
        </div>
      </div>`;
    drop.hidden = false;
    qIn.setAttribute('aria-expanded', 'true');
    const v = (k) => drop.querySelector(`[data-af="${k}"]`).value;
    drop.querySelector('[data-af-oneoff]').addEventListener('click', () => choose({ kind: 'oneoff', name: v('name'), hours: parseHours(v('hours')) }));
    drop.querySelector('[data-af-add]').addEventListener('click', async (ev) => {
      const b = ev.currentTarget;
      const hours = parseHours(v('hours'));
      const nm = titleCaseName(v('name'));
      if (!nm) { drawAddForm('Give it a name.'); return; }
      if (hours == null) { drawAddForm('Std hours as H:MM, e.g. 1:30.'); return; }
      if (byKey().has(keyOf(nm))) { const e = byKey().get(keyOf(nm)); close(); addLine(lineFromEntry(e)); return; }
      b.disabled = true; b.textContent = 'Adding…';
      try {
        const entry = await opts.onAddToCatalogue({ name: nm, service: v('service') || null, hours, unit: v('unit') || 'each' });
        const e = entry || { key: keyOf(nm), name: nm, hours, service: v('service') || null, sub: null, uses: 0, saved: true };
        catalogue.push(e);
        close();
        addLine(lineFromEntry(e));
      } catch (err) {
        drawAddForm(`Couldn’t add it — ${(err && err.message) || err}`);
      }
    });
    // Esc inside the form closes it too.
    drop.querySelectorAll('input, select').forEach((el) => el.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); qIn.focus(); } }));
  }
  function choose(o) {
    if (!o) return;
    if (o.kind === 'item') { close(); addLine(lineFromEntry(o.e)); return; }
    if (o.kind === 'add') { ui.adding = true; drawAddForm(); drop.querySelector('[data-af="name"]')?.focus(); return; }
    if (o.kind === 'oneoff') {
      const name = titleCaseName(o.name || ui.q.trim());
      if (!name) return;
      close();
      addLine({ item: name, kind: 'work', stdHours: o.hours != null ? o.hours : 1, qty: 1, complexity: 1, source: 'Track', oneOff: true });
    }
  }
  function close() { ui.open = false; ui.adding = false; ui.q = ''; ui.active = 0; if (qIn) qIn.value = ''; drawDrop(); }

  if (qIn) {
    // Opens as you type (an empty box is just the field — page 3 at rest).
    qIn.addEventListener('focus', () => { if (ui.q.trim()) { ui.open = true; drawDrop(); } });
    qIn.addEventListener('input', () => { ui.q = qIn.value; ui.open = true; ui.adding = false; ui.active = 0; drawDrop(); });
    qIn.addEventListener('blur', () => setTimeout(() => {
      if (drop.contains(document.activeElement)) return;
      if (ui.adding) return;
      ui.open = false; drawDrop();
    }, 150));
    qIn.addEventListener('keydown', (e) => {
      const { list } = options();
      if (e.key === 'ArrowDown') { e.preventDefault(); ui.open = true; ui.active = Math.min(list.length - 1, ui.active + 1); drawDrop(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); ui.active = Math.max(0, ui.active - 1); drawDrop(); }
      else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (ui.adding) return; choose(list[ui.active]); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    });
    document.addEventListener('mousedown', (e) => { if (!host.isConnected) return; if (ui.open && !host.querySelector('.fcx-search').contains(e.target)) { ui.open = false; ui.adding = false; drawDrop(); } });
  }
  if (aiBtn && opts.onDraftWithAi) {
    aiBtn.addEventListener('click', async () => {
      if (ui.aiBusy) return;
      if (lines.length && !window.confirm('Replace the lines with the AI draft?')) return;
      ui.aiBusy = true; aiBtn.disabled = true; aiBtn.textContent = 'Drafting…'; aiNote.hidden = true;
      try {
        const out = await opts.onDraftWithAi();
        if (out && Array.isArray(out.lines) && out.lines.length) {
          lines = out.lines.map((l) => ({ ...l }));
          draw(); changed();
        }
        if (out && out.note) { aiNote.textContent = out.note; aiNote.hidden = false; }
      } catch (err) {
        aiNote.textContent = `Draft with AI didn’t work — ${(err && err.message) || err}`; aiNote.hidden = false;
      } finally {
        ui.aiBusy = false; aiBtn.disabled = false; aiBtn.textContent = '✦ Draft with AI';
      }
    });
  }

  draw();
  return {
    state,
    totals,
    setLines(next, revisions) { lines = (next || []).map((l) => ({ ...l })); if (revisions) rev = Object.assign({ count: 1, percent: 20, charged: false }, revisions); draw(); changed(); },
    setSuggestions(list) { cooccur = Array.isArray(list) ? list : []; draw(); },
    setCatalogue(list) { catalogue = Array.isArray(list) ? list.slice() : []; draw(); },
    focusSearch() { qIn && qIn.focus(); },
  };
}
